import React, { useEffect, useState, MutableRefObject } from 'react'
import { AutocompleteInputChangeReason, TextField, TextFieldProps } from '@mui/material'
import { Autocomplete, AutocompleteProps } from '@mui/material'
import { FieldValues, useController, UseControllerProps } from 'react-hook-form'

export enum TriggerCharacter {
  CurlyBrace = '{',
  Parenthesis = '(',
  SquareBracket = '[',
  AngleBracket = '<',
  DoubleQuote = '"',
  SingleQuote = "'",
}

export type Option = {
  label: string
  value: string
}

type AutocompleteOption = Option | string

export type CustomAutocompleteProps<T extends FieldValues> = UseControllerProps<T> & {
  textFieldProps?: TextFieldProps
  options: Option[]
  triggerCharacter?: TriggerCharacter
  inputRef?: MutableRefObject<HTMLInputElement | null>
} & Omit<
    AutocompleteProps<Option, false, false, true>,
    'onChange' | 'value' | 'renderInput' | 'inputValue' | 'onInputChange' | 'options' | 'open' | 'defaultValue'
  >

export default function DynamicTextAutocomplete<T extends FieldValues>({
  name,
  control,
  rules,
  defaultValue,
  textFieldProps,
  options,
  triggerCharacter = TriggerCharacter.CurlyBrace,
  inputRef,
  ...autocompleteProps
}: CustomAutocompleteProps<T>) {
  const bracePairs: { [key in TriggerCharacter]: string } = {
    [TriggerCharacter.CurlyBrace]: '}',
    [TriggerCharacter.Parenthesis]: ')',
    [TriggerCharacter.SquareBracket]: ']',
    [TriggerCharacter.AngleBracket]: '>',
    [TriggerCharacter.DoubleQuote]: '"',
    [TriggerCharacter.SingleQuote]: "'",
  }

  const {
    field: { onChange, onBlur, value = '', ref },
    fieldState: { error },
  } = useController({ name, control, rules, defaultValue })

  const [open, setOpen] = useState<boolean>(false)
  const [localInputValue, setLocalInputValue] = useState<string>(value || '')

  useEffect(() => {
    if (value !== localInputValue) {
      setLocalInputValue(value)
    }
  }, [value])

  useEffect(() => {
    const shouldOpen = localInputValue.includes(triggerCharacter)
    if (shouldOpen !== open) {
      setOpen(shouldOpen)
    }
  }, [localInputValue, triggerCharacter, open])

  const getLastTriggerIndex = (text: string) => text.lastIndexOf(triggerCharacter)

  const getSearchText = (text: string) => {
    const lastIndex = getLastTriggerIndex(text)
    return lastIndex >= 0 ? text.slice(lastIndex + 1) : ''
  }

  const handleInputChange = (_: React.SyntheticEvent, newInputValue: string, reason: AutocompleteInputChangeReason) => {
    if (reason === 'reset') return
    setLocalInputValue(newInputValue)
    onChange(newInputValue)
  }

  const handleOptionSelect = (_: React.SyntheticEvent, newValue: AutocompleteOption | null) => {
    if (newValue && typeof newValue !== 'string') {
      const lastIndex = getLastTriggerIndex(localInputValue)
      const textBeforeTrigger = localInputValue.slice(0, lastIndex + 1)
      const closingChar = bracePairs[triggerCharacter] || ''
      const newText = `${textBeforeTrigger}${newValue.label}${closingChar}`

      setLocalInputValue(newText)
      onChange(newText)
      setOpen(false)
    }
  }

  return (
    <Autocomplete
      {...autocompleteProps}
      freeSolo
      value={localInputValue}
      open={open}
      options={options}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
      onClose={() => setOpen(false)}
      inputValue={localInputValue}
      onInputChange={handleInputChange}
      onChange={handleOptionSelect}
      filterOptions={(opts: Option[]) => {
        const searchText = getSearchText(localInputValue)
        return opts.filter((option) => option.label.toLowerCase().includes(searchText.toLowerCase()))
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          {...textFieldProps}
          fullWidth
          onBlur={onBlur}
          name={name}
          error={!!error}
          helperText={error ? error.message : null}
          inputRef={(el) => {
            ref(el)
            if (inputRef) inputRef.current = el
          }}
        />
      )}
    />
  )
}
